import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '../services/api';
import { Menu, X, Home, Search, PlusCircle, MessageCircle, User, LogOut, Package, Shield, Bell, Heart, Crown, Gift, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';

const Header = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const notifRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isAdmin = user?.role === 'admin';

  // Fetch notifications + push notification support
  useEffect(() => {
    if (!isAuthenticated) return;
    let prevUnread = 0;
    const fetchNotifs = async () => {
      try {
        const data = await getNotifications();
        setNotifications(data.notifications);
        setUnreadCount(data.unread_count);
        // Show browser push notification for new ones
        if (data.unread_count > prevUnread && prevUnread > 0 && Notification.permission === 'granted') {
          const newest = data.notifications.find(n => !n.read);
          if (newest) new Notification('NeighborBorrow', { body: newest.message, icon: '/favicon.ico' });
        }
        prevUnread = data.unread_count;
      } catch (err) {
        console.error('Failed to fetch notifications:', err);
      }
    };
    // Request push permission on first load
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleNotifClick = async (notif) => {
    if (!notif.read) {
      await markNotificationRead(notif.id);
      setUnreadCount((c) => Math.max(0, c - 1));
      setNotifications((prev) => prev.map((n) => n.id === notif.id ? { ...n, read: true } : n));
    }
    setShowNotifs(false);
    if (notif.link) navigate(notif.link);
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead();
    setUnreadCount(0);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <header className="bg-[#FDFBF7]/80 backdrop-blur-xl border-b border-[#EBE8DF] sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
            <div className="w-10 h-10 bg-[#D97757] rounded-full flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-semibold text-xl text-[#2C3329]">NeighborBorrow</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <Link to="/browse" className="flex items-center space-x-1 text-[#596652] hover:text-[#2C3329] transition-colors" data-testid="browse-link">
              <Search className="w-4 h-4" />
              <span>Browse</span>
            </Link>

            {isAuthenticated ? (
              <>
                <Link to="/list-item" className="flex items-center space-x-1 text-[#596652] hover:text-[#2C3329] transition-colors" data-testid="list-item-link">
                  <PlusCircle className="w-4 h-4" />
                  <span>List Item</span>
                </Link>
                <Link to="/messages" className="flex items-center space-x-1 text-[#596652] hover:text-[#2C3329] transition-colors" data-testid="messages-link">
                  <MessageCircle className="w-4 h-4" />
                  <span>Messages</span>
                </Link>
                <Link to="/dashboard" className="flex items-center space-x-1 text-[#596652] hover:text-[#2C3329] transition-colors" data-testid="dashboard-link">
                  <Package className="w-4 h-4" />
                  <span>Dashboard</span>
                </Link>
                {isAdmin && (
                  <Link to="/admin" className="flex items-center space-x-1 text-[#D97757] hover:text-[#C56547] transition-colors" data-testid="admin-link">
                    <Shield className="w-4 h-4" />
                    <span>Admin</span>
                  </Link>
                )}

                {/* Wishlist */}
                <Link to="/dashboard?tab=wishlist" className="relative text-[#596652] hover:text-[#D97757] transition-colors" data-testid="wishlist-header-link">
                  <Heart className="w-5 h-5" />
                </Link>

                {/* Membership */}
                <Link to="/membership" className="relative text-[#F59E0B] hover:text-[#D97757] transition-colors" data-testid="membership-header-link" title="Membership">
                  <Crown className="w-5 h-5" />
                </Link>

                {/* Notification Bell */}
                <div className="relative" ref={notifRef}>
                  <button
                    onClick={() => setShowNotifs(!showNotifs)}
                    className="relative text-[#596652] hover:text-[#2C3329] transition-colors p-1"
                    data-testid="notification-bell"
                  >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-4.5 h-4.5 min-w-[18px] bg-[#D97757] text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {showNotifs && (
                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl border border-[#EBE8DF] shadow-lg overflow-hidden z-50" data-testid="notification-dropdown">
                      <div className="flex items-center justify-between px-4 py-3 border-b border-[#EBE8DF]">
                        <span className="font-heading font-semibold text-[#2C3329]">Notifications</span>
                        {unreadCount > 0 && (
                          <button onClick={handleMarkAllRead} className="text-xs text-[#D97757] hover:text-[#C56547]" data-testid="mark-all-read">
                            Mark all read
                          </button>
                        )}
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <p className="px-4 py-6 text-center text-[#596652] text-sm">No notifications yet</p>
                        ) : (
                          notifications.slice(0, 10).map((notif) => (
                            <button
                              key={notif.id}
                              onClick={() => handleNotifClick(notif)}
                              className={`w-full text-left px-4 py-3 border-b border-[#EBE8DF] last:border-0 hover:bg-[#FDFBF7] transition-colors ${
                                !notif.read ? 'bg-[#D97757]/5' : ''
                              }`}
                              data-testid={`notification-item-${notif.id}`}
                            >
                              <div className="flex items-start gap-2">
                                {!notif.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-[#D97757] shrink-0" />}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-[#2C3329] truncate">{notif.title}</p>
                                  <p className="text-xs text-[#596652] truncate">{notif.message}</p>
                                  <p className="text-[10px] text-[#8C9A76] mt-0.5">{timeAgo(notif.created_at)}</p>
                                </div>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-3">
                  <Link to="/profile" className="flex items-center space-x-2" data-testid="profile-link">
                    <div className="w-8 h-8 bg-[#8C9A76] rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-[#2C3329] font-medium">{user?.full_name?.split(' ')[0]}</span>
                  </Link>
                  <Button variant="ghost" size="icon" onClick={handleLogout} className="text-[#596652] hover:text-[#D97757]" data-testid="logout-button">
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-3">
                <Link to="/login" data-testid="login-link">
                  <Button variant="ghost" className="text-[#596652] hover:text-[#2C3329]">Log in</Button>
                </Link>
                <Link to="/register" data-testid="register-link">
                  <Button className="bg-[#D97757] text-white rounded-full hover:bg-[#C56547]">Sign up</Button>
                </Link>
              </div>
            )}
          </nav>

          {/* Mobile menu button */}
          <div className="flex items-center gap-2 md:hidden">
            {isAuthenticated && (
              <button onClick={() => { setShowNotifs(!showNotifs); setMobileMenuOpen(false); }} className="relative p-2 text-[#596652]" data-testid="mobile-notification-bell">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-[#D97757] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            )}
            <button className="p-2" onClick={() => { setMobileMenuOpen(!mobileMenuOpen); setShowNotifs(false); }} data-testid="mobile-menu-button">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Notification Dropdown */}
        {showNotifs && (
          <div className="md:hidden py-2 border-t border-[#EBE8DF]">
            <div className="flex items-center justify-between px-2 py-2">
              <span className="font-heading font-semibold text-sm text-[#2C3329]">Notifications</span>
              {unreadCount > 0 && (
                <button onClick={handleMarkAllRead} className="text-xs text-[#D97757]">Mark all read</button>
              )}
            </div>
            <div className="max-h-60 overflow-y-auto">
              {notifications.length === 0 ? (
                <p className="px-2 py-4 text-center text-[#596652] text-sm">No notifications</p>
              ) : (
                notifications.slice(0, 8).map((notif) => (
                  <button key={notif.id} onClick={() => handleNotifClick(notif)} className={`w-full text-left px-2 py-2 rounded-lg ${!notif.read ? 'bg-[#D97757]/5' : ''}`}>
                    <p className="text-sm font-medium text-[#2C3329] truncate">{notif.title}</p>
                    <p className="text-xs text-[#596652] truncate">{notif.message}</p>
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-[#EBE8DF]">
            <nav className="flex flex-col space-y-3">
              <Link to="/browse" className="flex items-center space-x-2 px-2 py-2 text-[#596652] hover:bg-[#8C9A76]/10 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-browse-link">
                <Search className="w-5 h-5" /><span>Browse Items</span>
              </Link>
              {isAuthenticated ? (
                <>
                  <Link to="/list-item" className="flex items-center space-x-2 px-2 py-2 text-[#596652] hover:bg-[#8C9A76]/10 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-list-item-link">
                    <PlusCircle className="w-5 h-5" /><span>List Item</span>
                  </Link>
                  <Link to="/messages" className="flex items-center space-x-2 px-2 py-2 text-[#596652] hover:bg-[#8C9A76]/10 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-messages-link">
                    <MessageCircle className="w-5 h-5" /><span>Messages</span>
                  </Link>
                  <Link to="/dashboard" className="flex items-center space-x-2 px-2 py-2 text-[#596652] hover:bg-[#8C9A76]/10 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-dashboard-link">
                    <Package className="w-5 h-5" /><span>Dashboard</span>
                  </Link>
                  <Link to="/dashboard?tab=wishlist" className="flex items-center space-x-2 px-2 py-2 text-[#596652] hover:bg-[#8C9A76]/10 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-wishlist-link">
                    <Heart className="w-5 h-5" /><span>Wishlist</span>
                  </Link>
                  <Link to="/membership" className="flex items-center space-x-2 px-2 py-2 text-[#F59E0B] hover:bg-[#F59E0B]/10 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-membership-link">
                    <Crown className="w-5 h-5" /><span>Membership</span>
                  </Link>
                  <Link to="/referral" className="flex items-center space-x-2 px-2 py-2 text-[#D97757] hover:bg-[#D97757]/10 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-referral-link">
                    <Gift className="w-5 h-5" /><span>Refer & Earn</span>
                  </Link>
                  <Link to="/disputes" className="flex items-center space-x-2 px-2 py-2 text-[#596652] hover:bg-[#8C9A76]/10 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-disputes-link">
                    <AlertTriangle className="w-5 h-5" /><span>Disputes</span>
                  </Link>
                  <Link to="/profile" className="flex items-center space-x-2 px-2 py-2 text-[#596652] hover:bg-[#8C9A76]/10 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-profile-link">
                    <User className="w-5 h-5" /><span>Profile</span>
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" className="flex items-center space-x-2 px-2 py-2 text-[#D97757] hover:bg-[#D97757]/10 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-admin-link">
                      <Shield className="w-5 h-5" /><span>Admin Panel</span>
                    </Link>
                  )}
                  <button className="flex items-center space-x-2 px-2 py-2 text-[#D97757] hover:bg-[#D97757]/10 rounded-lg w-full" onClick={() => { handleLogout(); setMobileMenuOpen(false); }} data-testid="mobile-logout-button">
                    <LogOut className="w-5 h-5" /><span>Log out</span>
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="flex items-center space-x-2 px-2 py-2 text-[#596652] hover:bg-[#8C9A76]/10 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-login-link">
                    <User className="w-5 h-5" /><span>Log in</span>
                  </Link>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-register-link">
                    <Button className="w-full bg-[#D97757] text-white rounded-full hover:bg-[#C56547]">Sign up</Button>
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
